/**
 * Stripe Checkout redirect for Pack Leader (web / PWA / Android).
 */

import {
  WAGAMOUR_PLATFORM_HEADER,
  WAGAMOUR_PLATFORM_IOS_NATIVE,
} from '@/lib/monetization/iapConstants'
import {
  logUpgradeBlockedDuringNativeOAuth,
  shouldBlockUpgradeDuringNativeOAuth,
  UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE,
} from '@/lib/monetization/blockUpgradeDuringNativeOAuth'
import { detectNativeIosForUpgrade } from '@/lib/monetization/detectUpgradePlatform'
import { logUpgradeFlow } from '@/lib/monetization/upgradeFlowDebugLog'

export interface StripeCheckoutOptions {
  userId?: string
  feature?: string
  email?: string
  name?: string
}

export type StripeCheckoutResult =
  | { ok: true; redirecting: true }
  | { ok: false; error: string; status?: number }

export async function startStripeCheckout(
  options: StripeCheckoutOptions
): Promise<StripeCheckoutResult> {
  const isNativeIosUpgrade = detectNativeIosForUpgrade()

  console.log('[UpgradeTrace9419bd7] startStripeCheckout.enter', {
    feature: options.feature,
    hasUserId: Boolean(options.userId),
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
    isNativeIosUpgrade,
  })

  if (shouldBlockUpgradeDuringNativeOAuth()) {
    logUpgradeBlockedDuringNativeOAuth('startStripeCheckout')
    console.log('[UpgradeTrace9419bd7] startStripeCheckout.exit', { path: 'blocked_native_oauth' })
    return {
      ok: false,
      error: UPGRADE_BLOCKED_DURING_OAUTH_MESSAGE,
    }
  }

  if (isNativeIosUpgrade) {
    logUpgradeFlow('Stripe path blocked on native iOS', { isNativeIosUpgrade })
    console.log('[UpgradeTrace9419bd7] startStripeCheckout.exit', { path: 'blocked_native_ios' })
    return {
      ok: false,
      error: 'Apple In-App Purchase is required on the iOS app.',
    }
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    [WAGAMOUR_PLATFORM_HEADER]: isNativeIosUpgrade
      ? WAGAMOUR_PLATFORM_IOS_NATIVE
      : 'web',
  }
  if (options.userId) {
    headers['X-User-Id'] = options.userId
  }

  const response = await fetch('/api/subscription/checkout', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      feature: options.feature,
      userId: options.userId,
      email: options.email,
      name: options.name,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorMessage =
      (errorData as { error?: string }).error || 'Failed to create checkout session'
    console.log('[UpgradeTrace9419bd7] startStripeCheckout.exit', {
      path: 'fetch_failed',
      status: response.status,
      error: errorMessage,
    })
    return { ok: false, error: errorMessage, status: response.status }
  }

  const data = (await response.json()) as { url?: string; checkoutUrl?: string }
  const checkoutUrl = data.url || data.checkoutUrl
  if (!checkoutUrl) {
    console.log('[UpgradeTrace9419bd7] startStripeCheckout.exit', { path: 'missing_checkout_url' })
    return { ok: false, error: 'Missing checkout URL' }
  }

  console.log('[UpgradeTrace9419bd7] startStripeCheckout.redirect', {
    checkoutUrl,
    pathname: typeof window !== 'undefined' ? window.location.pathname : null,
  })
  window.location.href = checkoutUrl
  return { ok: true, redirecting: true }
}

/** Headers for non-iOS checkout requests (explicit platform marker). */
export function stripeCheckoutHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    [WAGAMOUR_PLATFORM_HEADER]: 'web',
  }
}

export function iosNativePlatformHeader(): Record<string, string> {
  return {
    [WAGAMOUR_PLATFORM_HEADER]: WAGAMOUR_PLATFORM_IOS_NATIVE,
  }
}
